import React, { useState, useEffect, useCallback } from "react";
import { cronConfigService } from "../../../service/admin/cronConfigService";
import { formatDate } from "../../../helper/utils";
import Loader from "../../../component/common/loader";
import {
  FaSync,
  FaPlay,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaClock,
  FaCogs,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaEdit,
} from "react-icons/fa";

const STATUS_COLORS = {
  success: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  running: "bg-blue-100 text-blue-800",
};

const STATUS_ICONS = {
  success: <FaCheckCircle className="text-green-500" />,
  failed: <FaTimesCircle className="text-red-500" />,
  running: <FaSpinner className="animate-spin text-blue-500" />,
};

// Common cron schedule presets
const SCHEDULE_PRESETS = [
  { label: "Every 5 minutes", value: "*/5 * * * *" },
  { label: "Every 10 minutes", value: "*/10 * * * *" },
  { label: "Every 15 minutes", value: "*/15 * * * *" },
  { label: "Every 30 minutes", value: "*/30 * * * *" },
  { label: "Every hour", value: "0 * * * *" },
  { label: "Every 2 hours", value: "0 */2 * * *" },
  { label: "Every 6 hours", value: "0 */6 * * *" },
  { label: "Every 12 hours", value: "0 */12 * * *" },
  { label: "Daily at midnight", value: "0 0 * * *" },
  { label: "Daily at 6 AM", value: "0 6 * * *" },
];

export default function CronManagement() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(null);
  const [testSku, setTestSku] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [whiteBgStats, setWhiteBgStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cronConfigService.getAllCronConfigs();
      if (response?.status?.code === 200) {
        setConfigs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching cron configs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchWhiteBgStats();
  }, [fetchConfigs]);

  const fetchWhiteBgStats = async () => {
    try {
      setStatsLoading(true);
      const response = await cronConfigService.getWhiteBgStats();
      if (response?.status?.code === 200) {
        setWhiteBgStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching white-bg stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleTestSku = async () => {
    if (!testSku.trim()) return;
    try {
      setTestLoading(true);
      setTestResult(null);
      const response = await cronConfigService.testWhiteBgSingleSku(testSku.trim());
      if (response?.status?.code === 200) {
        setTestResult(response.data);
        fetchWhiteBgStats();
      } else {
        setTestResult({ action: "error", reason: response?.status?.message || "Failed" });
      }
    } catch (error) {
      setTestResult({ action: "error", reason: error.message });
    } finally {
      setTestLoading(false);
    }
  };

  const handleToggle = async (cronKey) => {
    try {
      const response = await cronConfigService.toggleCronStatus(cronKey);
      if (response?.status?.code === 200) {
        fetchConfigs();
      }
    } catch (error) {
      console.error("Error toggling cron:", error);
    }
  };

  const handleTrigger = async (cronKey) => {
    try {
      setTriggering(cronKey);
      const response = await cronConfigService.triggerCronManually(cronKey);
      if (response?.status?.code === 200) {
        alert("Cron job triggered successfully!");
        fetchConfigs();
      }
    } catch (error) {
      console.error("Error triggering cron:", error);
      alert("Failed to trigger cron job");
    } finally {
      setTriggering(null);
    }
  };

  const startEdit = (config) => {
    setEditingKey(config.cronKey);
    setEditForm({
      schedule: config.schedule,
      accountTypeFilter: { ...config.accountTypeFilter },
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditForm({});
  };

  const handleSave = async (cronKey) => {
    try {
      setSaving(true);
      const response = await cronConfigService.updateCronConfig(cronKey, editForm);
      if (response?.status?.code === 200) {
        setEditingKey(null);
        setEditForm({});
        fetchConfigs();
      }
    } catch (error) {
      console.error("Error saving cron config:", error);
      alert("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleChange = (value) => {
    setEditForm((prev) => ({ ...prev, schedule: value }));
  };

  const handleAccountTypeChange = (field) => {
    setEditForm((prev) => ({
      ...prev,
      accountTypeFilter: {
        ...prev.accountTypeFilter,
        [field]: !prev.accountTypeFilter[field],
      },
    }));
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cron Management</h1>
          <p className="text-gray-500 mt-1">
            Configure cron schedules and account type filters
          </p>
        </div>
        <button
          onClick={fetchConfigs}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <FaSync /> Refresh
        </button>
      </div>

      <div className="grid gap-6">
        {configs.map((config) => (
          <div
            key={config.cronKey}
            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-3">
                <FaCogs className="text-gray-500 text-xl" />
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {config.displayName}
                  </h3>
                  <p className="text-sm text-gray-500">{config.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Status Badge */}
                {config.lastRunStatus && (
                  <span
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      STATUS_COLORS[config.lastRunStatus] || "bg-gray-100"
                    }`}
                  >
                    {STATUS_ICONS[config.lastRunStatus]}
                    {config.lastRunStatus}
                  </span>
                )}
                {/* Toggle */}
                <button
                  onClick={() => handleToggle(config.cronKey)}
                  className={`flex items-center gap-2 px-3 py-1 rounded ${
                    config.isEnabled
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {config.isEnabled ? (
                    <FaToggleOn className="text-xl" />
                  ) : (
                    <FaToggleOff className="text-xl" />
                  )}
                  {config.isEnabled ? "Enabled" : "Disabled"}
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-4">
              {editingKey === config.cronKey ? (
                /* Edit Mode */
                <div className="space-y-4">
                  {/* Schedule */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule (Cron Expression)
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={editForm.schedule}
                        onChange={(e) => handleScheduleChange(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="*/15 * * * *"
                      />
                      <select
                        onChange={(e) => handleScheduleChange(e.target.value)}
                        className="px-3 py-2 border rounded-lg bg-white"
                        value=""
                      >
                        <option value="">Quick Select...</option>
                        {SCHEDULE_PRESETS.map((preset) => (
                          <option key={preset.value} value={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Account Type Filter */}
                  {(config.cronKey === "amazonOrderPull" ||
                    config.cronKey === "amazonTrackingUpdate" ||
                    config.cronKey === "amazonPriceUpdate" ||
                    config.cronKey === "amazonQuantityUpdate") && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Type Filter
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.accountTypeFilter?.ownAccount}
                            onChange={() => handleAccountTypeChange("ownAccount")}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span>Own Account (BTPL)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.accountTypeFilter?.resellerAccount}
                            onChange={() =>
                              handleAccountTypeChange("resellerAccount")
                            }
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span>Reseller Account</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.accountTypeFilter?.otherAccounts}
                            onChange={() =>
                              handleAccountTypeChange("otherAccounts")
                            }
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span>Other Accounts</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => handleSave(config.cronKey)}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-3">
                  <div className="flex items-center gap-6">
                    <div>
                      <span className="text-sm text-gray-500">Schedule:</span>
                      <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                        {config.schedule}
                      </span>
                    </div>
                    {config.lastRunAt && (
                      <div>
                        <span className="text-sm text-gray-500">Last Run:</span>
                        <span className="ml-2 text-sm">
                          {formatDate(config.lastRunAt)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Account Type Filter Display */}
                  {(config.cronKey === "amazonOrderPull" ||
                    config.cronKey === "amazonTrackingUpdate" ||
                    config.cronKey === "amazonPriceUpdate" ||
                    config.cronKey === "amazonQuantityUpdate") && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        Account Types:
                      </span>
                      <div className="flex gap-2">
                        {config.accountTypeFilter?.ownAccount && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            Own Account
                          </span>
                        )}
                        {config.accountTypeFilter?.resellerAccount && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                            Reseller
                          </span>
                        )}
                        {config.accountTypeFilter?.otherAccounts && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            Others
                          </span>
                        )}
                        {!config.accountTypeFilter?.ownAccount &&
                          !config.accountTypeFilter?.resellerAccount &&
                          !config.accountTypeFilter?.otherAccounts && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                              None Selected
                            </span>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => startEdit(config)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded hover:bg-gray-200 text-sm"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() => handleTrigger(config.cronKey)}
                      disabled={triggering === config.cronKey}
                      className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm disabled:opacity-50"
                    >
                      {triggering === config.cronKey ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        <FaPlay />
                      )}
                      Run Now
                    </button>
                  </div>

                  {/* White BG Test & Stats - only for whiteBackgroundProcess */}
                  {config.cronKey === "whiteBackgroundProcess" && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                      {/* Stats */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Processing Stats</span>
                          <button
                            onClick={fetchWhiteBgStats}
                            disabled={statsLoading}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {statsLoading ? "Loading..." : "Refresh"}
                          </button>
                        </div>
                        {whiteBgStats ? (
                          <div className="grid grid-cols-4 gap-3">
                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-blue-700">{whiteBgStats.totalProducts?.toLocaleString()}</div>
                              <div className="text-xs text-blue-500">Total</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-green-700">{whiteBgStats.processed?.toLocaleString()}</div>
                              <div className="text-xs text-green-500">Processed</div>
                            </div>
                            <div className="bg-orange-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-orange-700">{whiteBgStats.pending?.toLocaleString()}</div>
                              <div className="text-xs text-orange-500">Pending</div>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-purple-700">{whiteBgStats.completionPercent}%</div>
                              <div className="text-xs text-purple-500">Complete</div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400">Loading stats...</div>
                        )}
                      </div>

                      {/* Test Single SKU */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Test Single SKU</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={testSku}
                            onChange={(e) => setTestSku(e.target.value)}
                            placeholder="Enter SKU e.g. 992111643088"
                            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            onKeyDown={(e) => e.key === "Enter" && handleTestSku()}
                          />
                          <button
                            onClick={handleTestSku}
                            disabled={testLoading || !testSku.trim()}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm disabled:opacity-50"
                          >
                            {testLoading ? <FaSpinner className="animate-spin" /> : <FaPlay />}
                            Test
                          </button>
                        </div>
                        {testResult && (
                          <div className={`mt-2 p-3 rounded-lg text-sm ${
                            testResult.action === "error" ? "bg-red-50 text-red-700" :
                            testResult.action === "swapped" || testResult.action === "marked" ? "bg-green-50 text-green-700" :
                            "bg-yellow-50 text-yellow-700"
                          }`}>
                            <span className="font-medium">{testResult.sku}</span> — {testResult.action}: {testResult.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {configs.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FaClock className="text-4xl mx-auto mb-3" />
          <p>No cron configurations found</p>
        </div>
      )}
    </div>
  );
}
